import { Form, Formik, useField, useFormikContext } from "formik";
import * as Yup from "yup";
import { yearRange } from "../validation";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import type { Client } from "@/features/clients/types";
import type { FormSubmission } from "@/features/forms/types";
import { FormEditorActions } from "./form-editor-actions";
import { FormikCell, FormikCheckbox } from "./formik-controls";

// --- Co-located data type (base POA shape) ----------------------------------
// snake_case keys mirror the backend mapper `apps/forms/mappings/form_idpoa.py`
// `build_defaults` exactly (the `data` blob is passed through verbatim). Taxpayer
// identity + the single representative's contact details are filled live from the
// Client / Practitioner records server-side, never carried in this blob.

/** Which party the ID-POA is filed for. */
export type IdpoaForWho = "taxpayer" | "spouse" | "joint";
/** Revoke-prior-POA selection: revoke all / keep / revoke listed. */
export type IdpoaRevokeType = "A" | "K" | "S";

export interface Form_idpoa_Data {
  for_who: IdpoaForWho;
  // All Tax Types & Fees.
  type_all: boolean;
  permit_all: string;
  all_all: boolean;
  period_all: string;
  // Individual/Business Income Tax.
  type_income: boolean;
  all_income: boolean;
  period_income: string;
  // Sales & Use Tax.
  type_sales: boolean;
  permit_sales: string;
  all_sales: boolean;
  period_sales: string;
  // Withholding.
  type_withhold: boolean;
  permit_withhold: string;
  all_withhold: boolean;
  period_withhold: string;
  // Other tax/fee.
  type_other: boolean;
  other_desc: string;
  permit_other: string;
  all_other: boolean;
  period_other: string;
  // Copies + cease.
  no_copies: boolean;
  cease_date: string;
  // Revoke prior POAs.
  revoke_type: IdpoaRevokeType;
  revoke_desc: string;
  // Business signer title.
  signer_title: string;
}

const FOR_WHO_OPTIONS: { value: IdpoaForWho; label: string }[] = [
  { value: "taxpayer", label: "Taxpayer" },
  { value: "spouse", label: "Spouse" },
  { value: "joint", label: "Jointly" },
];

const REVOKE_OPTIONS: { value: IdpoaRevokeType; label: string }[] = [
  { value: "K", label: "Keep all prior powers of attorney in effect" },
  { value: "A", label: "Revoke all prior powers of attorney" },
  { value: "S", label: "Revoke only the powers of attorney listed below" },
];

const schema = Yup.object({
  for_who: Yup.string().oneOf(["taxpayer", "spouse", "joint"]).required(),
  revoke_type: Yup.string().oneOf(["A", "K", "S"]).required(),
  permit_all: Yup.string().max(40, "Too long"),
  permit_sales: Yup.string().max(40, "Too long"),
  permit_withhold: Yup.string().max(40, "Too long"),
  permit_other: Yup.string().max(40, "Too long"),
  other_desc: Yup.string().max(120, "Too long"),
  period_all: yearRange,
  period_income: yearRange,
  period_sales: yearRange,
  period_withhold: yearRange,
  period_other: yearRange,
});

function toInitialValues(data: Record<string, unknown>): Form_idpoa_Data {
  const d = data as Partial<Form_idpoa_Data>;
  return {
    for_who: d.for_who ?? "taxpayer",
    type_all: d.type_all ?? true,
    permit_all: d.permit_all ?? "",
    all_all: d.all_all ?? true,
    period_all: d.period_all ?? "",
    type_income: d.type_income ?? false,
    all_income: d.all_income ?? true,
    period_income: d.period_income ?? "",
    type_sales: d.type_sales ?? false,
    permit_sales: d.permit_sales ?? "",
    all_sales: d.all_sales ?? true,
    period_sales: d.period_sales ?? "",
    type_withhold: d.type_withhold ?? false,
    permit_withhold: d.permit_withhold ?? "",
    all_withhold: d.all_withhold ?? true,
    period_withhold: d.period_withhold ?? "",
    type_other: d.type_other ?? false,
    other_desc: d.other_desc ?? "",
    permit_other: d.permit_other ?? "",
    all_other: d.all_other ?? true,
    period_other: d.period_other ?? "",
    no_copies: d.no_copies ?? false,
    cease_date: d.cease_date ?? "",
    revoke_type: d.revoke_type ?? "K",
    revoke_desc: d.revoke_desc ?? "",
    signer_title: d.signer_title ?? "",
  };
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return <h3 className="text-foreground border-b pb-2 text-lg font-bold">{children}</h3>;
}

function ForWhoField() {
  const [field, , helpers] = useField<IdpoaForWho>("for_who");
  return (
    <RadioGroup
      className="flex flex-wrap gap-6"
      value={field.value}
      onValueChange={(v) => helpers.setValue(v as IdpoaForWho)}
    >
      {FOR_WHO_OPTIONS.map((o) => (
        <Label key={o.value} htmlFor={`fw-${o.value}`} className="flex items-center gap-2 font-normal">
          <RadioGroupItem id={`fw-${o.value}`} value={o.value} />
          {o.label}
        </Label>
      ))}
    </RadioGroup>
  );
}

/** A specific tax-type row: enable toggle, optional permit, all-periods vs range. */
function TaxTypeRow({
  typeName,
  label,
  permitName,
  allName,
  periodName,
}: {
  typeName: keyof Form_idpoa_Data;
  label: string;
  permitName?: keyof Form_idpoa_Data;
  allName: keyof Form_idpoa_Data;
  periodName: keyof Form_idpoa_Data;
}) {
  const { values } = useFormikContext<Form_idpoa_Data>();
  const enabled = !!values[typeName];
  const allPeriods = !!values[allName];
  return (
    <div className="flex flex-col gap-3 rounded-md border p-4">
      <FormikCheckbox name={typeName} label={label} />
      {enabled && (
        <div className="flex flex-col gap-3 pl-6">
          {permitName && (
            <div className="grid max-w-xs gap-1.5">
              <Label htmlFor={permitName} className="text-muted-foreground text-xs">
                Permit / Account number
              </Label>
              <FormikCell name={permitName} aria-label={`${label} permit number`} />
            </div>
          )}
          <FormikCheckbox name={allName} label="All periods" />
          {!allPeriods && (
            <div className="grid max-w-xs gap-1.5">
              <Label htmlFor={periodName} className="text-muted-foreground text-xs">
                Specific period(s)
              </Label>
              <FormikCell
                name={periodName}
                placeholder="e.g. 2021, 2022"
                aria-label={`${label} periods`}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function OtherTaxTypeRow() {
  const { values } = useFormikContext<Form_idpoa_Data>();
  const enabled = values.type_other;
  const allPeriods = values.all_other;
  return (
    <div className="flex flex-col gap-3 rounded-md border p-4">
      <FormikCheckbox name="type_other" label="Other tax or fee" />
      {enabled && (
        <div className="flex flex-col gap-3 pl-6">
          <div className="grid max-w-xs gap-1.5">
            <Label htmlFor="other_desc" className="text-muted-foreground text-xs">
              Describe the tax or fee
            </Label>
            <FormikCell name="other_desc" aria-label="Other tax description" />
          </div>
          <div className="grid max-w-xs gap-1.5">
            <Label htmlFor="permit_other" className="text-muted-foreground text-xs">
              Permit / Account number
            </Label>
            <FormikCell name="permit_other" aria-label="Other permit number" />
          </div>
          <FormikCheckbox name="all_other" label="All periods" />
          {!allPeriods && (
            <div className="grid max-w-xs gap-1.5">
              <Label htmlFor="period_other" className="text-muted-foreground text-xs">
                Specific period(s)
              </Label>
              <FormikCell name="period_other" placeholder="e.g. 2021, 2022" aria-label="Other periods" />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** All Tax Types & Fees, or the specific-type mix (mutually exclusive). */
function AuthorizationSection({ isBusiness }: { isBusiness: boolean }) {
  const { values } = useFormikContext<Form_idpoa_Data>();
  const allTypes = values.type_all;
  const allPeriods = values.all_all;
  return (
    <Card className="gap-4 p-6">
      <SectionHeading>Tax Types Authorized</SectionHeading>
      <div className="flex flex-col gap-3 rounded-md border p-4">
        <FormikCheckbox name="type_all" label="All tax types and fees" />
        {allTypes && (
          <div className="flex flex-col gap-3 pl-6">
            <div className="grid max-w-xs gap-1.5">
              <Label htmlFor="permit_all" className="text-muted-foreground text-xs">
                Permit / Account number
              </Label>
              <FormikCell name="permit_all" aria-label="All types permit number" />
            </div>
            <FormikCheckbox name="all_all" label="All periods" />
            {!allPeriods && (
              <div className="grid max-w-xs gap-1.5">
                <Label htmlFor="period_all" className="text-muted-foreground text-xs">
                  Specific period(s)
                </Label>
                <FormikCell name="period_all" placeholder="e.g. 2021, 2022" aria-label="All types periods" />
              </div>
            )}
          </div>
        )}
      </div>

      {!allTypes && (
        <div className="flex flex-col gap-3">
          <p className="text-muted-foreground text-sm">
            Or select the specific tax types this authorization covers:
          </p>
          <TaxTypeRow
            typeName="type_income"
            label={isBusiness ? "Business income tax" : "Individual income tax"}
            allName="all_income"
            periodName="period_income"
          />
          <TaxTypeRow
            typeName="type_sales"
            label="Sales & use tax"
            permitName="permit_sales"
            allName="all_sales"
            periodName="period_sales"
          />
          <TaxTypeRow
            typeName="type_withhold"
            label="Withholding tax"
            permitName="permit_withhold"
            allName="all_withhold"
            periodName="period_withhold"
          />
          <OtherTaxTypeRow />
        </div>
      )}
    </Card>
  );
}

function RevokeSection() {
  const [field, , helpers] = useField<IdpoaRevokeType>("revoke_type");
  const showList = field.value === "S";
  return (
    <Card className="gap-4 p-6">
      <SectionHeading>Prior Powers of Attorney</SectionHeading>
      <RadioGroup
        className="flex flex-col gap-2"
        value={field.value}
        onValueChange={(v) => helpers.setValue(v as IdpoaRevokeType)}
      >
        {REVOKE_OPTIONS.map((o) => (
          <Label key={o.value} htmlFor={`rv-${o.value}`} className="flex items-center gap-2 font-normal">
            <RadioGroupItem id={`rv-${o.value}`} value={o.value} />
            {o.label}
          </Label>
        ))}
      </RadioGroup>
      {showList && (
        <div className="grid gap-1.5">
          <Label htmlFor="revoke_desc" className="text-muted-foreground text-xs">
            List the powers of attorney to revoke
          </Label>
          <FormikCell name="revoke_desc" aria-label="Powers of attorney to revoke" />
        </div>
      )}
    </Card>
  );
}

function CeaseDateField() {
  const [field] = useField("cease_date");
  return (
    <div className="grid max-w-xs gap-1.5">
      <Label htmlFor="cease_date">Authorization end date (optional)</Label>
      <Input id="cease_date" type="date" {...field} />
    </div>
  );
}

interface Props {
  client: Client;
  submission: FormSubmission;
  onSubmissionChange: (submission: FormSubmission) => void;
}

/**
 * Editable Form ID-POA (Idaho Power of Attorney) — the base state POA skeleton:
 * a taxpayer identity block (prefilled from the client), the client's lead
 * representative (contact details filled server-side), a tax-type authorization
 * section, an optional end date + copies toggle, and a revoke-prior-POA section.
 */
export function FormIdpoaEditor({ client, submission, onSubmissionChange }: Props) {
  const isBusiness = client.type === "Business";
  const address = [
    client.homeStreet,
    [client.homeCity, [client.homeState, client.homeZip].filter(Boolean).join(" ")]
      .filter(Boolean)
      .join(", "),
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <Formik
      initialValues={toInitialValues(submission.data)}
      validationSchema={schema}
      onSubmit={() => {}}
    >
      <Form className="flex flex-col gap-6">
        <Card className="gap-6 p-6">
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-4">
              <span className="text-foreground font-semibold">Filed for</span>
              <ForWhoField />
            </div>
            <div className="text-muted-foreground text-sm">
              <p className="text-foreground font-medium">{client.name}</p>
              {address && <p>{address}</p>}
            </div>
          </div>
        </Card>

        <section className="flex flex-col gap-4">
          <SectionHeading>Representative</SectionHeading>
          <p className="text-muted-foreground text-sm">
            The client's lead practitioner is named on this form. Their contact,
            firm, and PTIN details are filled from the practitioner record. Assign
            or reorder practitioners on the client's profile.
          </p>
        </section>

        <AuthorizationSection isBusiness={isBusiness} />

        <Card className="gap-4 p-6">
          <SectionHeading>Copies & Duration</SectionHeading>
          <FormikCheckbox
            name="no_copies"
            label="Do not send copies of notices and communications to the representative"
          />
          <CeaseDateField />
        </Card>

        <RevokeSection />

        <FormEditorActions
          submission={submission}
          clientId={client.id}
          onSubmissionChange={onSubmissionChange}
        />
      </Form>
    </Formik>
  );
}
